import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Coins, Calendar, Receipt, Truck } from "lucide-react";
import { formatDate } from "@/lib/utils";

const tomorrow = () => new Date().toISOString().slice(0, 7);
const months = (() => {
  const arr = []; const d = new Date();
  for (let i = 0; i < 18; i++) {
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return arr;
})();
const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DriverPayouts() {
  const [month, setMonth] = useState(tomorrow());
  const [data, setData] = useState({ rows: [], summary: { total: 0, order_count: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/driver/payouts", { params: month ? { month } : {} });
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--gold)" }}>Meine Vergütungen</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Pauschalbeträge</h1>
            <p className="text-sm text-[var(--text-2)] mt-1">Übersicht über vereinbarte Beträge pro Auftrag.</p>
          </div>
          <select
            data-testid="driver-payouts-month"
            className="input-base max-w-[160px]"
            value={month}
            onChange={e => setMonth(e.target.value)}
          >
            <option value="">Alle Monate</option>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </header>

        {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><Coins size={12} /> Total</div>
            <div className="text-2xl md:text-3xl font-semibold mt-2" data-testid="driver-payouts-total">{chf(data.summary.total)}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><Receipt size={12} /> Aufträge</div>
            <div className="text-2xl md:text-3xl font-semibold mt-2">{data.summary.order_count}</div>
          </div>
        </div>

        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Auftrag</th>
                <th className="text-left py-3 px-4">Datum</th>
                <th className="text-left py-3 px-4">Strecke</th>
                <th className="text-right py-3 px-4">Pal.</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">CHF</th>
              </tr>
            </thead>
            <tbody data-testid="driver-payouts-table">
              {loading && <tr><td colSpan={6} className="text-center py-10 text-[var(--text-3)]">Lade…</td></tr>}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[var(--text-3)]">Keine Vergütungen im gewählten Zeitraum.</td></tr>
              )}
              {!loading && data.rows.map(r => (
                <tr key={r.id} className="border-b border-[var(--border)]">
                  <td className="py-3 px-4 font-mono text-[var(--text)]">
                    <Link to={`/driver/orders/${r.id}`} className="hover:text-[#D4AF37]">{r.order_number}</Link>
                  </td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{formatDate(r.delivery_date)}</td>
                  <td className="py-3 px-4 text-[var(--text-2)] text-xs">{r.from} → {r.to}</td>
                  <td className="py-3 px-4 text-right font-mono text-[var(--text-2)]">{r.pallet_count}</td>
                  <td className="py-3 px-4 text-[var(--text-3)] text-xs uppercase tracking-wider">{r.status}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold" style={{ color: "var(--header)" }}>{Number(r.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
